import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiSearch } from "react-icons/fi";

import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import BlogCard from "./BlogCard";
import { BLOG_POSTS } from "./posts";

const Blogs = () => {
  const [query, setQuery] = useState("");

  // Client-side filter: the list is small and fully local, so there is nothing
  // to fetch and the results can update as the visitor types.
  const posts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return BLOG_POSTS;

    return BLOG_POSTS.filter(({ title, excerpt }) =>
      `${title} ${excerpt}`.toLowerCase().includes(term)
    );
  }, [query]);

  return (
    <>
      <Helmet>
        <title>Blogs | Zero Typing AI</title>
        <meta
          name="description"
          content="Practical guides, product tips, AI insights, and voice typing workflows to help you turn spoken thoughts into clean, ready-to-use text."
        />
        <link rel="canonical" href="https://zerotyping.ai/blogs" />
      </Helmet>

      <div className="w-full bg-white font-inter">
        <Navbar />

        {/* Header */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-[96px] pt-28 md:pt-36">
          <h1 className="mx-auto max-w-[860px] text-center font-jakarta font-bold text-[#0A0D16] text-[30px] sm:text-[38px] md:text-[44px] lg:text-[50px] leading-[1.28] tracking-[-0.8px]">
            Ideas For Working Faster, Writing Better, And Typing Less
          </h1>

          <p className="mx-auto mt-[21.46px] max-w-[700px] text-center font-inter text-[16px] font-normal leading-[29.6px] tracking-[0px] text-[#0A0D16B2]">
            Explore practical guides, productivity tips, AI insights, and voice
            typing workflows designed to help you turn spoken thoughts into
            clean, ready-to-use text.
          </p>

          {/* Search */}
          <form
            role="search"
            onSubmit={(event) => event.preventDefault()}
            className="mx-auto mt-[30.05px] flex w-full max-w-[520px] items-center gap-2 rounded-full border-[1.07px] border-[#E6ECF5] bg-white p-[6px] pl-[18px] shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
          >
            <FiSearch
              className="h-[16px] w-[16px] shrink-0 text-[#0A0D1666]"
              aria-hidden="true"
            />

            <label htmlFor="blog-search" className="sr-only">
              Search articles
            </label>
            <input
              id="blog-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search articles..."
              className="min-w-0 flex-1 bg-transparent font-inter text-[14px] font-normal leading-[24px] text-[#0A0D16] outline-none placeholder:text-[#0A0D1666] [&::-webkit-search-cancel-button]:appearance-none"
            />

            <button
              type="submit"
              className="shrink-0 cursor-pointer rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-[22px] py-[9px] font-inter text-[14px] font-semibold text-white transition-all duration-300 hover:from-[#33A2E5] hover:to-[#386FE8]"
            >
              Search
            </button>
          </form>
        </div>

        {/* Listing */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-[96px] pb-[64px] pt-[40px] md:pb-[88px] md:pt-[48px] lg:pb-[112px]">
          {posts.length === 0 ? (
            <p className="py-[48px] text-center font-inter text-[16px] leading-[29.6px] text-[#0A0D16B2]">
              No articles match &ldquo;{query.trim()}&rdquo;.
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-[20px] sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <li key={post.id} className="flex">
                  <BlogCard post={post} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Blogs;
